import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useMemo, useState } from "react";
import { useTranslations } from "use-intl";

import { DrawerTopBar } from "@/components/Drawer/DrawerTopBar";
import { SwipeableDrawer } from "@/components/Drawer/SwipeableDrawer";
import { SelectedAdditionalServicesCard } from "@/features/flights/components/flight-purchase/AdditionalServicesSelection/SelectedAdditionalServicesCard";
import BottomDrawer from "@/features/flights/components/flight-purchase/BottomDrawer";
import { useFlightSearchDataContext } from "@/features/flights/contexts/FlightSearchDataContext";
import { useFlightSelectionPayloadContext } from "@/features/flights/contexts/FlightSelectionPayloadContext/FlightSelectionPayloadContext";
import { flightAvailabilityQueryOptions } from "@/features/flights/hooks/useFlightAvailability";
import {
  getAvailableServices,
  getCheapestMandatoryServices,
} from "@/features/flights/utils/additionalServices";
import { TravellerSelectionStatusDrawer } from "@/features/travellers/components/TravellerSelectionStatusDrawer";
import TravellersList from "@/features/travellers/components/TravellersList";
import { TravellersSelection } from "@/features/travellers/components/TravellersSelection";
import { useTravellersSelectionContext } from "@/features/travellers/contexts/TravellersSelectionContext";
import { travellersQueryOptions } from "@/features/travellers/hooks/useTravellers";
import { getTravellerTypeAtTravelDate } from "@/features/travellers/utils/ageValidation";
import { travelerTypeToTypeCode } from "@/features/travellers/utils/transformTraveler";
import { FlightTrackingEvents, rudderAnalytics } from "@/lib/rudderstack";
import { type Traveller, type TravellerAgeGroup } from "@/types/types";
import { Feature, isFeatureDisabled } from "@/utils/featureFlags";

export const Route = createFileRoute(
  "/_authenticated/_flights/_flight-offer/flights/offer/$id/step/2/",
)({
  component: RouteComponent,
  staticData: {
    drawer: true,
  },
});

function RouteComponent() {
  const t = useTranslations();
  const navigate = Route.useNavigate();
  const { searchData } = useFlightSearchDataContext();
  const { getTravellerServices, setTravellerServices, selectedFlightPayload } =
    useFlightSelectionPayloadContext();
  const { data: singleFlightDetails, isLoading: isAdditionalDataLoading } =
    useQuery(flightAvailabilityQueryOptions(selectedFlightPayload));

  const {
    travellerSlots,
    selectedTravellers,
    selectTraveller,
    getTravellerIndex,
  } = useTravellersSelectionContext();

  const { data: travellers } = useQuery(travellersQueryOptions());

  const getServicesInfo = useCallback(
    (traveller: Traveller, overrideTravellerIndex?: number) => {
      const availableServices = getAvailableServices({
        traveller,
        travellerIndex:
          overrideTravellerIndex !== undefined
            ? overrideTravellerIndex
            : getTravellerIndex(traveller.id),
        optionalServices: singleFlightDetails?.optionalServices,
        mandatoryServices: singleFlightDetails?.mandatoryServices,
      });

      const selectedServices = getTravellerServices(traveller.id);

      const availableMandatoryServiceCount = availableServices.filter(
        (service) => service.mandatory,
      ).length;

      // null if there are no mandatory services to select from, boolean otherwise
      const hasSelectedAllMandatoryServices =
        availableMandatoryServiceCount > 0
          ? selectedServices.filter((service) => service.mandatory).length ===
            availableMandatoryServiceCount
          : null;

      return {
        availableServices,
        selectedServices,
        selectedServicesCount: selectedServices.length,
        hasSelectedAllMandatoryServices,
      };
    },
    [
      getTravellerIndex,
      getTravellerServices,
      singleFlightDetails?.mandatoryServices,
      singleFlightDetails?.optionalServices,
    ],
  );

  const hasSelectedMandatoryServicesForAllTravellers = useMemo(() => {
    if (isFeatureDisabled(Feature.AdditionalServiceSelection)) {
      return true;
    }

    return selectedTravellers.every((traveller) => {
      const servicesInfo = getServicesInfo(traveller);

      // Either all mandatory services are selected, or there are no mandatory services to select from
      return (
        servicesInfo.hasSelectedAllMandatoryServices === null ||
        servicesInfo.hasSelectedAllMandatoryServices === true
      );
    });
  }, [selectedTravellers, getServicesInfo]);

  const travelDate = new Date(searchData!.dates.departure);

  const [selectedSlot, setSelectedSlot] = useState<{
    ageGroup: TravellerAgeGroup;
    index: number;
  } | null>(null);

  const travellersToSelect =
    travellerSlots.ADT.length +
    travellerSlots.CHD.length +
    travellerSlots.INF.length;

  const allTravellersSelected =
    travellerSlots.ADT.every(Boolean) &&
    travellerSlots.CHD.every(Boolean) &&
    travellerSlots.INF.every(Boolean);

  return (
    <>
      <div className="px-4">
        <TravellersSelection
          onSlotClick={(ageGroup, index) => {
            setSelectedSlot({ ageGroup, index });
          }}
          renderAdditionalServiceControls={(traveller) => {
            if (isFeatureDisabled(Feature.AdditionalServiceSelection)) {
              return null;
            }

            const { availableServices, selectedServicesCount } =
              getServicesInfo(traveller);

            if (availableServices.length === 0) {
              return null;
            }

            return (
              <SelectedAdditionalServicesCard
                servicesSelectedCount={selectedServicesCount}
                onClick={() => {
                  navigate({
                    to: "./additional-services",
                    search: {
                      travellerId: traveller.id,
                    },
                  });
                }}
              />
            );
          }}
        />

        <SwipeableDrawer
          hasFullHeight
          heightOffset={24}
          open={!!selectedSlot}
          onClose={() => setSelectedSlot(null)}
          backgroundColor="var(--color-surface-background)"
        >
          <DrawerTopBar
            onCloseClick={() => setSelectedSlot(null)}
            title={
              selectedSlot
                ? t("travellers.addFor", {
                    type: t("travellers.travellerTypeN", {
                      type: selectedSlot.ageGroup,
                      number: selectedSlot.index + 1,
                    }),
                  })
                : null
            }
          />
          {selectedSlot ? (
            <TravellersList
              travellers={(travellers || []).filter(
                (traveller) =>
                  travelerTypeToTypeCode(
                    getTravellerTypeAtTravelDate(
                      traveller.birthDate,
                      travelDate,
                    ),
                  ) === selectedSlot.ageGroup,
              )}
              travelDate={travelDate}
              selectedTravellerId={
                travellerSlots[selectedSlot.ageGroup][selectedSlot.index]
              }
              isSelectionDisabled={(traveller) =>
                selectedTravellers.some((t) => t.id === traveller.id)
              }
              onTravellerSelected={(travellerId) => {
                selectTraveller(selectedSlot.index, travellerId);
                setSelectedSlot(null);

                const traveller = travellers?.find((t) => t.id === travellerId);

                if (
                  !traveller ||
                  isFeatureDisabled(Feature.AdditionalServiceSelection)
                )
                  return;

                const servicesInfo = getServicesInfo(
                  traveller,
                  selectedSlot.index,
                );
                // We're selecting the cheapest mandatory services for traveller being selected to streamline the journey
                if (
                  servicesInfo.hasSelectedAllMandatoryServices === null ||
                  servicesInfo.hasSelectedAllMandatoryServices !== true
                ) {
                  const availableServices = servicesInfo.availableServices;
                  const selectedServices =
                    getCheapestMandatoryServices(availableServices);
                  setTravellerServices(traveller.id, selectedServices);
                }
              }}
            />
          ) : null}
        </SwipeableDrawer>
      </div>

      <TravellerSelectionStatusDrawer
        travellersSelected={selectedTravellers.length}
        travellersToSelect={travellersToSelect}
      />

      <BottomDrawer
        buttonLabel={t("flights.continue")}
        onButtonClick={() => {
          rudderAnalytics.track(
            FlightTrackingEvents.USER_CONTINUED_ON_TRAVELERS,
            {
              passenger_count: selectedTravellers.length,
            },
          );

          navigate({ to: "/flights/offer/$id/step/3" });
        }}
        disabled={
          !allTravellersSelected ||
          !hasSelectedMandatoryServicesForAllTravellers ||
          isAdditionalDataLoading
        }
      />
    </>
  );
}
